import React, { useState } from 'react';
import { IInvoiceFullObject } from '../Interfaces/IInvoice';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  Typography,
} from '@mui/material';
import { CustomeTextField } from '../generic/CustomeTextField';
import {
  AccountBalance,
  Description,
  Download,
  FindInPage,
  WorkspacePremium,
} from '@mui/icons-material';
import { IDocument } from '../Interfaces/IDocument';
import { base64Download } from '../Document/DocumentRow';
import { useSelector } from 'react-redux';
import { State } from '../redux/mainReducer';
import { getFetch } from '../hooks/useFetch';
import { DocumentPreviewDialog } from '../Document/DocumentAssistant/DocumentPreviewDialog';
import { ILegalTransactionType } from '../Interfaces/ILegalTransactionType';
import { IinvoiceState } from '../Interfaces/IInvoiceState';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { InvoicePaidTable } from './InvoicePaidTable';
import { InvoicePaidPlanTable } from './InvoicePaidPlanTable';
import { DebtorAutocomplete } from './DebtorAutocomplete';
import { DebtorWarningBars } from './DebtorWarningBars';
import QRCode from 'react-qr-code';
import { useGetLegalTransactionFullObject } from '../hooks/reactQuerys/LegalTransactionQueries';

interface IProps {
  editInvoice: IInvoiceFullObject;
  setEditInvoice: Function;
  legalTransactionTypes: ILegalTransactionType[];
  invoiceStates: IinvoiceState[];
  legalClients: ILegalClient[];
  markNewPaids?: boolean;
}

export const InvoiceEdit: React.FC<IProps> = ({
  editInvoice,
  setEditInvoice,
  legalTransactionTypes,
  invoiceStates,
  legalClients,
  markNewPaids,
}) => {
  const scopes = localStorage.getItem('scope')?.split(',') ?? [];
  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );
  //
  const {
    legalTransactionFullObject,
    isFetchingLegalTransactionFullObject,
    refetchLegalTransactionFullObject,
  } = useGetLegalTransactionFullObject(editInvoice.idLegalTransaction);
  //
  const [localDocumentObject, setLocalDocumentObject] =
    useState<IDocument | null>(null);
  const [isOpenPreview, setisOpenPreview] = useState(false);
  //
  const [isFilterByLegalTransaction, setIsFilterByLegalTransaction] =
    useState(true);
  // #1197: Objekte der per Server-Suche gewählten Schuldner merken, damit deren
  // Chips ihr Label behalten (sie sind in keiner lokalen Mandantenliste).
  const [extraDebtorObjects, setExtraDebtorObjects] = useState<ILegalClient[]>(
    []
  );
  //
  const currentState = invoiceStates.find(
    state => state.idInvoiceState === editInvoice.idInvoiceState
  );

  // #1197: Vorgangs-Mandanten MIT Namen. Achtung: editInvoice.
  // LegalTransactionLegalClients sind nur Verknüpfungszeilen (IDs + Flags, ohne
  // FirstName/LastName) und taugen NICHT als Label-Quelle. Namen kommen aus dem
  // LegalClientArray des Vorgangs (bzw. der legalClients-Prop) und aus den per
  // Server-Suche gewählten Objekten.
  const vorgangClients: ILegalClient[] =
    legalTransactionFullObject?.LegalClientArray ?? legalClients ?? [];

  // Pool zum Auflösen der Schuldner-Objekte (Chips + Warnleisten #1183/#1185).
  const debtorPool = Array.from(
    new Map(
      [...vorgangClients, ...extraDebtorObjects].map(client => [
        client.idLegalClient,
        client,
      ])
    ).values()
  );
  const selectedDebtors = debtorPool.filter(client =>
    editInvoice.LegalClients.includes(client.idLegalClient)
  );

  const handleDownload = async () => {
    if (localDocumentObject !== null && localDocumentObject.Data) {
      base64Download(localDocumentObject, mimeTypeArray);
    } else {
      getFetch(
        '/document/',
        editInvoice.idDocument,
        (localDocumentObject: IDocument) => {
          setLocalDocumentObject(localDocumentObject);
          base64Download(localDocumentObject, mimeTypeArray);
        }
      );
    }
  };

  const handleOpenPreview = () => {
    if (localDocumentObject === null || !localDocumentObject.Data) {
      getFetch('/document/', editInvoice.idDocument, setLocalDocumentObject);
    }
    setisOpenPreview(true);
  };

  return (
    <>
      <DocumentPreviewDialog
        open={isOpenPreview}
        localDocumentObject={localDocumentObject}
        onClose={() => setisOpenPreview(false)}
      />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <CustomeTextField
            label='Zahlungsziel'
            attr='PaymentDueDate'
            object={editInvoice}
            setObject={setEditInvoice}
            type='date'
            required
          />
        </Grid>
        <Grid item xs={3}>
          <Box sx={{ mt: 1 }}>
            Erstellt am: {new Date(editInvoice.Created_at).toLocaleString()}
          </Box>
        </Grid>
        <Grid item xs={3}>
          <Button
            variant={editInvoice.isDeadlineSuspended ? 'contained' : 'outlined'}
            sx={{ float: 'right' }}
            onClick={() =>
              setEditInvoice({
                ...editInvoice,
                isDeadlineSuspended: !editInvoice.isDeadlineSuspended,
              })
            }
          >
            {!editInvoice.isDeadlineSuspended ? (
              <>Zahlungsziel Hemmen</>
            ) : (
              <>Hemmung aufgehoben</>
            )}
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, mb: 2 }}>
        <Collapse in={currentState?.isCloseing}>
          <Alert severity='warning'>
            Durch diesen Status wird die Rechnung geschlossen!
          </Alert>
        </Collapse>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <CustomeTextField
            attr='idInvoiceState'
            label='Status'
            object={editInvoice}
            setObject={setEditInvoice}
            type='select'
          >
            {invoiceStates.map(x => (
              <MenuItem
                key={`invoice-state-${x.idInvoiceState}`}
                value={x.idInvoiceState}
              >
                {x.InvoiceState}
              </MenuItem>
            ))}
          </CustomeTextField>
        </Grid>

        <Grid item xs={6}>
          <CustomeTextField
            attr='idLegalTransactionType'
            label='Art'
            object={editInvoice}
            setObject={setEditInvoice}
            type='select'
            required
          >
            {legalTransactionTypes.map(x => (
              <MenuItem
                key={`legalTransactionTypes-state-${x.idLegalTransactionType}`}
                value={x.idLegalTransactionType}
              >
                {x.LegalTransactionType}
              </MenuItem>
            ))}
          </CustomeTextField>
        </Grid>

        <Grid item xs={4}>
          <CustomeTextField
            attr='InvoiceNumber'
            label='Rechnungsnummer'
            object={editInvoice}
            setObject={setEditInvoice}
            type='string'
            required
          />
        </Grid>
        <Grid item xs={8}>
          <CustomeTextField
            attr='Total'
            label='Rechnungssumme'
            object={editInvoice}
            setObject={setEditInvoice}
            type='euro'
          />
        </Grid>

        {/* #1183 + #1185: Warnleisten (kein Schuldner / Schuldner ohne bzw. mit
            deaktivierter E-Mail) über dem Schuldner-Feld. */}
        <DebtorWarningBars debtors={selectedDebtors} layout='gridItem' />
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={isFilterByLegalTransaction}
                onChange={() =>
                  setIsFilterByLegalTransaction(!isFilterByLegalTransaction)
                }
              />
            }
            label='Nur Mandanten des Vorgangs'
            sx={{ float: 'right' }}
          />
        </Grid>
        <Grid item xs={12}>
          <DebtorAutocomplete
            serverSearch={!isFilterByLegalTransaction}
            options={vorgangClients}
            value={selectedDebtors}
            onChange={selected => {
              // Gewählte Objekte cachen (Labels) + IDs als Quelle der Wahrheit.
              setExtraDebtorObjects(prev =>
                Array.from(
                  new Map(
                    [...prev, ...selected].map(c => [c.idLegalClient, c])
                  ).values()
                )
              );
              setEditInvoice({
                ...editInvoice,
                LegalClients: selected.map(c => c.idLegalClient),
              });
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant='h6' sx={{ mt: 5 }}>
                Zugeordnete Objekte
              </Typography>
            </Grid>

            <Grid
              item
              xs={3}
              sx={{
                alignItems: 'center',
                justifyItems: 'center',
              }}
            >
              <QRCode
                value={String(editInvoice.EPCString)}
                style={{ height: 'auto', maxWidth: '100', width: '100' }}
              />
            </Grid>
            {legalTransactionFullObject && (
              <Grid item xs={3}>
                <Card variant='outlined'>
                  <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <WorkspacePremium />
                      <Typography>Urkundennummer</Typography>
                    </Box>
                    <CustomeTextField
                      label='Urkundennummer'
                      attr='idLegalTransactionRecordNumber'
                      object={editInvoice}
                      setObject={setEditInvoice}
                      type='select'
                      null
                    >
                      {legalTransactionFullObject.RecordNumberArray.map(
                        record => (
                          <MenuItem
                            key={`select-recordnummer-${record.idLegalTransactionRecordNumber}`}
                            value={record.idLegalTransactionRecordNumber}
                          >
                            {record.RecordNumberEntry}
                          </MenuItem>
                        )
                      )}
                    </CustomeTextField>
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid item xs={3}>
              <Card
                variant='outlined'
                sx={{
                  height: '47%',
                  cursor: scopes.includes('legaltransaction')
                    ? 'pointer'
                    : undefined,
                }}
                onClick={() => {
                  window.open(
                    `/legaltransaction/${editInvoice.idLegalTransaction}`,
                    '_blank'
                  );
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <AccountBalance />
                    <Typography>Vorgang</Typography>
                  </Box>
                  <Typography>{editInvoice.Title}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={3}>
              <Card variant='outlined' sx={{ height: '47%' }}>
                <CardContent>
                  <Box sx={{ float: 'right' }}>
                    <IconButton onClick={handleOpenPreview}>
                      <FindInPage />
                    </IconButton>
                    <IconButton size='small' onClick={handleDownload}>
                      <Download />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Description />
                    <Typography>Dokument</Typography>
                  </Box>

                  <Typography>{editInvoice.FileName}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography variant='h6' sx={{ mt: 5 }}>
            Zahlungen
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <InvoicePaidPlanTable
            editInvoice={editInvoice}
            setEditInvoice={setEditInvoice}
          />
        </Grid>

        <Grid item xs={6}>
          <InvoicePaidTable
            editInvoice={editInvoice}
            setEditInvoice={setEditInvoice}
            markNewPaids={markNewPaids}
          />
        </Grid>
      </Grid>
    </>
  );
};
